import React, { useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaDownload, FaCheckSquare, FaRegSquare, FaSpinner, FaFileArchive, FaMinus, FaExpandAlt } from "react-icons/fa";

const USER_CATEGORIES = [
  { key: "profile",          label: "Profile & Due Dates",         desc: "Account, Birthdate, All Services Due Dates, feedback & photo" },
  { key: "chats",            label: "Chat History",                desc: "All messages with admin" },
  { key: "documents",        label: "Documents",                   desc: "Files by folder, RA docs & meeting recordings" },
  { key: "folders",          label: "Folders",                     desc: "Folder structure" },
  { key: "invoices",         label: "Invoices",                    desc: "Invoice files & report" },
  { key: "payments",         label: "Payments",                    desc: "Payments, quick payment cards & unlock status" },
  { key: "workflow",         label: "Workflow / Stepcards",        desc: "All steps, answers, history & checklists" },
  { key: "stepFiles",        label: "Step Uploads & Signed Docs",  desc: "Files uploaded in steps + signed documents", isNew: true },
  { key: "bookkeeping",      label: "Bookkeeping",                 desc: "Queries, Step 3 uploads, steps & schedule", isNew: true },
  { key: "financialReports", label: "Financial Reports",           desc: "Zoho-synced & uploaded P&L / Balance Sheet", isNew: true },
  { key: "reports",          label: "Annual Report Data",          desc: "Annual report tables & filing history" },
  { key: "meetings",         label: "Meetings & Recordings",       desc: "Meeting list with recording files", isNew: true },
  { key: "referrals",        label: "Referrals & Promo Codes",     desc: "Referral records, credits & promo usage", isNew: true },
  { key: "reminders",        label: "Reminders",                   desc: "Chat & email reminders with attachments", isNew: true },
  { key: "emails",           label: "Email History",               desc: "Bulk & campaign emails sent to the user", isNew: true },
  { key: "logs",             label: "Activity Logs",               desc: "Login history & admin impersonation" },
  { key: "notifications",    label: "Notifications",               desc: "All notifications" },
  { key: "tickets",          label: "Support Tickets",             desc: "Support & query history" },
  { key: "attachments",      label: "Chat Attachments",            desc: "Files sent in chat" },
];

const PLATFORM_CATEGORIES = [
  { key: "platformLeads",      label: "Leads & Contacts",       desc: "Leads, contacts, webinar registrations, queries", isNew: true },
  { key: "platformTeam",       label: "Employees & Admins",     desc: "Team accounts & assigned clients (no passwords)", isNew: true },
  { key: "platformMarketing",  label: "Campaigns & Promo Codes",desc: "Campaigns, logs, templates, events, bulk emails", isNew: true },
  { key: "platformContent",    label: "Website Content",        desc: "Blogs, questions & all CMS pages", isNew: true },
  { key: "platformRecycleBin", label: "Recycle Bin",            desc: "Deleted items backup", isNew: true },
];

const ExportUserModal = ({ isOpen, onClose, user, isAllExport, selectedUserIds }) => {
  const EXPORT_CATEGORIES = isAllExport ? [...USER_CATEGORIES, ...PLATFORM_CATEGORIES] : USER_CATEGORIES;
  const [options, setOptions] = useState(
    [...USER_CATEGORIES, ...PLATFORM_CATEGORIES].reduce((acc, c) => ({ ...acc, [c.key]: true }), {})
  );
  const [loading, setLoading]   = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [progress, setProgress]  = useState(0); // 0-100
  const [error, setError]        = useState("");
  const [isMinimized, setIsMinimized] = useState(false);

  if (!isOpen) return null;

  const selected    = EXPORT_CATEGORIES.filter(c => options[c.key]).length;
  const allSelected = selected === EXPORT_CATEGORIES.length;
  const noneSelected = selected === 0;

  const toggleAll = () => {
    const newVal = !allSelected;
    setOptions(prev => EXPORT_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: newVal }), { ...prev }));
  };

  const buildPayloadOptions = () =>
    EXPORT_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: !!options[c.key] }), {});

  const renderCategory = (cat) => (
    <button
      key={cat.key}
      type="button"
      onClick={() => toggle(cat.key)}
      disabled={loading}
      title={cat.desc}
      className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-left text-sm transition-all duration-150 disabled:opacity-40 min-h-[4rem] ${
        options[cat.key]
          ? "bg-indigo-600/20 border-indigo-500/60 text-white"
          : "bg-gray-800/60 border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300"
      }`}
    >
      <div className="mt-0.5 flex-shrink-0">
        {options[cat.key]
          ? <FaCheckSquare className="text-indigo-400" size={14} />
          : <FaRegSquare className="text-gray-600" size={14} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm leading-snug flex items-center gap-2">
          {cat.label}
          {cat.isNew && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
              New
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">{cat.desc}</div>
      </div>
    </button>
  );

  const toggle = (key) => setOptions(prev => ({ ...prev, [key]: !prev[key] }));

  const handleClose = () => {
    if (loading) return;
    onClose();
    setError("");
    setStatusMsg("");
    setProgress(0);
    setIsMinimized(false);
  };

  const handleDownload = async () => {
    if (noneSelected) {
      setError("Please select at least one category.");
      return;
    }

    setLoading(true);
    setError("");
    setProgress(10);
    setStatusMsg(
      isAllExport
        ? (selectedUserIds && selectedUserIds.length > 0 
            ? `Collecting data for ${selectedUserIds.length} selected users…`
            : "Collecting all platform data…")
        : `Collecting data for ${user?.name || "user"}…`
    );

    try {
      const token    = localStorage.getItem("adminToken");
      const endpoint = isAllExport
        ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/export-all`
        : `${process.env.REACT_APP_API_BASE_URL}/api/admin/export-user`;

      const body = isAllExport
        ? { exportOptions: buildPayloadOptions(), userIds: selectedUserIds }
        : { userId: user._id, exportOptions: buildPayloadOptions() };

      setProgress(20);

      const response = await fetch(endpoint, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      setProgress(40);

      // Try to parse JSON error if response is not OK
      if (!response.ok) {
        let errMsg = `Server returned ${response.status}`;
        try {
          const contentType = response.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const errData = await response.json();
            errMsg = errData.message || errMsg;
          } else {
            errMsg = await response.text() || errMsg;
          }
        } catch (_) { /* ignore parse error */ }
        throw new Error(errMsg);
      }

      // Confirm we got a ZIP back
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("zip") && !contentType.includes("octet-stream")) {
        // Might be a JSON error body that slipped through
        try {
          const errData = await response.json();
          throw new Error(errData.message || "Unexpected response from server");
        } catch (_) { /* not json */ }
      }

      setStatusMsg("Downloading file…");
      setProgress(55);

      // ZIP is streamed by the server, so total size is unknown — show bytes received
      // and ease the bar toward 95% as data arrives.
      let blob;
      if (response.body && typeof response.body.getReader === "function") {
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;
        let lastUpdate = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          const now = Date.now();
          if (now - lastUpdate > 250) {
            lastUpdate = now;
            const mb = received / 1048576;
            setStatusMsg(`Downloading… ${mb.toFixed(1)} MB received`);
            setProgress(Math.min(95, Math.round(55 + 40 * (1 - Math.exp(-mb / 10)))));
          }
        }
        blob = new Blob(chunks, { type: "application/zip" });
      } else {
        blob = await response.blob();
      }

      setProgress(95);

      // Construct a sensible filename
      const safeName = (isAllExport ? "platform" : (user?.name || "user")).replace(/[^a-zA-Z0-9]/g, "_");
      const date     = new Date().toISOString().slice(0, 10);
      const filename = isAllExport
        ? `platform-export-${date}.zip`
        : `user-export-${safeName}-${date}.zip`;

      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href     = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setProgress(100);
      setStatusMsg("✅ Download complete!");

      setTimeout(() => {
        handleClose();
      }, 1800);

    } catch (err) {
      console.error("Export error:", err);
      setError(err.message || "Export failed. Please try again.");
      setStatusMsg("");
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  // Render Minimized Dock
  if (isMinimized) {
    return createPortal(
      <div className="fixed bottom-6 right-6 z-[1300] w-80 bg-gray-900 border border-indigo-500/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto">
        {/* Header (Docked) */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-900/60 to-purple-900/60 border-b border-gray-700/50">
          <div className="flex items-center gap-2 overflow-hidden">
            <FaFileArchive className="text-indigo-400 flex-shrink-0" size={14} />
            <span className="text-xs font-semibold text-white truncate">
              Exporting: {isAllExport ? "Platform Data" : (user?.name || "User Data")}
            </span>
          </div>
          <div className="flex items-center gap-1.5 ml-2">
            <button
              onClick={() => setIsMinimized(false)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
              title="Restore to full view"
            >
              <FaExpandAlt size={12} />
            </button>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors disabled:opacity-30"
              title="Close"
            >
              <FaTimes size={12} />
            </button>
          </div>
        </div>

        {/* Body (Docked) */}
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Progress</span>
            <span className="text-[10px] font-bold text-indigo-400">{progress}%</span>
          </div>
          
          <div className="h-1.5 w-full bg-gray-800 rounded-full overflow-hidden mb-2">
            <div
              className={`h-full transition-all duration-500 rounded-full ${progress === 100 ? 'bg-green-500' : 'bg-indigo-500'}`}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex items-center gap-2 text-[11px] text-gray-300 bg-gray-800/50 rounded-lg px-2 py-1.5 border border-gray-700/30">
            {loading && progress < 100 && <FaSpinner className="animate-spin text-indigo-400 flex-shrink-0" size={10} />}
            <span className="truncate">{statusMsg || "Awaiting download..."}</span>
          </div>

          {error && (
            <div className="mt-2 p-2 rounded-lg bg-red-900/20 border border-red-500/20 text-red-400 text-[10px]">
              ⚠️ {error}
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1300] overflow-y-auto bg-black/70 backdrop-blur-sm pointer-events-auto"
      onClick={(e) => { if (e.target === e.currentTarget && !loading) handleClose(); }}
    >
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
      <div
        className="relative w-full max-w-3xl bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[min(88vh,calc(100vh-3rem))]"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gradient-to-r from-indigo-900/60 to-purple-900/60 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <FaFileArchive className="text-indigo-400" size={18} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">
                {isAllExport ? "Export All Platform Data" : "Export User Data"}
              </h2>
              {!isAllExport && user && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {user.name} — {user.email}
                </p>
              )}
              {isAllExport && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedUserIds?.length > 0
                    ? `Exports data for ${selectedUserIds.length} selected users + platform records`
                    : "Exports data for every user on the platform + platform records"}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Minimize Button */}
            {loading && (
              <button
                onClick={() => setIsMinimized(true)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                title="Minimize and stay here"
              >
                <FaMinus size={15} />
              </button>
            )}
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-40"
              title="Close"
            >
              <FaTimes size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">

          {/* Select All */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-300">
              Select categories  
              <span className="ml-2 text-xs text-gray-500">({selected}/{EXPORT_CATEGORIES.length} selected)</span>
            </span>
            <button
              onClick={toggleAll}
              disabled={loading}
              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-600/30 text-indigo-300 hover:bg-indigo-600/50 border border-indigo-600/30 transition-colors disabled:opacity-40"
            >
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          </div>

          {/* Checkboxes */}
          {isAllExport && (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Per-user data {selectedUserIds?.length > 0 ? `(${selectedUserIds.length} selected users)` : "(every user)"}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {USER_CATEGORIES.map(renderCategory)}
          </div>

          {isAllExport && (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Platform data (not tied to one user)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {PLATFORM_CATEGORIES.map(renderCategory)}
              </div>
            </>
          )}

          {/* Progress bar */}
          {(loading || progress > 0) && (
            <div className="mb-4">
              <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Status */}
          {statusMsg && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-indigo-900/30 border border-indigo-700/40 text-indigo-300 text-sm flex items-center gap-2">
              {loading && <FaSpinner className="animate-spin flex-shrink-0" size={12} />}
              {statusMsg}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-red-900/30 border border-red-700/40 text-red-300 text-sm">
              ⚠️ {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-t border-gray-700 bg-gray-900/60 shrink-0">
          <p className="text-xs text-gray-500">
            Output: ZIP with PDF/Excel + JSON files{isAllExport ? " + summary sheet" : " + manifest"}
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              disabled={loading || noneSelected}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 transition-colors disabled:opacity-40 flex items-center gap-2"
            >
              {loading
                ? <><FaSpinner className="animate-spin" size={13} /> Exporting…</>
                : <><FaDownload size={13} /> Download ZIP</>}
            </button>
          </div>
        </div>

      </div>
      </div>
    </div>,
    document.body
  );
};

export default ExportUserModal;
